import React, { useEffect, useMemo, useState } from 'react';
import Header from '../layout/Header';
import Button from '../common/Button';
import Modal from '../common/Modal';
import Table, { TableRow, TableCell } from '../common/Table';
import TextField from '../common/TextField';
import PickupAPI from '../../firebase/apis/api-pickup';
import { getCoaches } from '../../firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../common/Toast';
import { 
  Search, 
  Users, 
  GraduationCap, 
  Plus, 
  UserCheck, 
  ChevronRight, 
  AlertCircle,
  CheckCircle,
  X,
  School,
  XCircle
} from 'lucide-react';

// Grade bands as specified
const GRADE_BANDS = [
  'Kindergarten / 1st Grade',
  '2nd / 3rd Grade', 
  '4th / 5th Grade',
  'Middle School — 6th / 7th / 8th Grade'
];

// Helper to map grade to grade band
const mapGradeToBand = (grade) => {
  if (!grade) return null;
  const normalizedGrade = String(grade).toLowerCase().trim();
  
  if (normalizedGrade === 'kindergarten' || normalizedGrade === 'k' || 
      normalizedGrade === '1st' || normalizedGrade === 'first' || 
      normalizedGrade === '1' || normalizedGrade === '1st grade') {
    return 'Kindergarten / 1st Grade';
  }
  
  if (normalizedGrade === '2nd' || normalizedGrade === 'second' || 
      normalizedGrade === '2' || normalizedGrade === '2nd grade' ||
      normalizedGrade === '3rd' || normalizedGrade === 'third' || 
      normalizedGrade === '3' || normalizedGrade === '3rd grade') {
    return '2nd / 3rd Grade';
  }
  
  if (normalizedGrade === '4th' || normalizedGrade === 'fourth' || 
      normalizedGrade === '4' || normalizedGrade === '4th grade' ||
      normalizedGrade === '5th' || normalizedGrade === 'fifth' || 
      normalizedGrade === '5' || normalizedGrade === '5th grade') {
    return '4th / 5th Grade';
  }
  
  if (normalizedGrade === '6th' || normalizedGrade === 'sixth' || 
      normalizedGrade === '6' || normalizedGrade === '6th grade' ||
      normalizedGrade === '7th' || normalizedGrade === 'seventh' || 
      normalizedGrade === '7' || normalizedGrade === '7th grade' ||
      normalizedGrade === '8th' || normalizedGrade === 'eighth' || 
      normalizedGrade === '8' || normalizedGrade === '8th grade') {
    return 'Middle School — 6th / 7th / 8th Grade';
  }
  
  return null;
};

const SchoolRosters = () => {
  const { user } = useAuth();
  const adminId = user?.uid || user?.id || 'system';
  const { success, error, warning } = useToast();

  // State
  const [loading, setLoading] = useState(true);
  const [schools, setSchools] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState('');
  const [selectedSchoolId, setSelectedSchoolId] = useState('');
  const [schoolStudents, setSchoolStudents] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [gradeFilter, setGradeFilter] = useState('');
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  
  // Searchable dropdown states
  const [schoolSearchTerm, setSchoolSearchTerm] = useState('');
  const [coachSearchTerm, setCoachSearchTerm] = useState('');
  const [isSchoolDropdownOpen, setIsSchoolDropdownOpen] = useState(false);
  const [isCoachDropdownOpen, setIsCoachDropdownOpen] = useState(false);
  
  // Modal states
  const [isCreateRosterModalOpen, setIsCreateRosterModalOpen] = useState(false);
  const [selectedGradeBand, setSelectedGradeBand] = useState('');
  const [selectedCoach, setSelectedCoach] = useState('');
  const [rosterName, setRosterName] = useState('');
  const [isCreatingRoster, setIsCreatingRoster] = useState(false);

  // Pickup rosters state
  const [pickupRosters, setPickupRosters] = useState([]);
  const [loadingRosters, setLoadingRosters] = useState(false);
  const [viewMode, setViewMode] = useState('create'); // 'create' or 'view'

  // Load schools
  const loadSchools = async () => {
    try {
      const data = await PickupAPI.getAllSchools({ isActive: true });
      setSchools(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('❌ Error loading schools:', e);
      error('Failed to load schools');
    }
  };

  // Load school students for selected school
  const loadSchoolStudents = async () => {
    if (!selectedSchoolId) {
      setSchoolStudents([]);
      return;
    }

    try {
      setLoadingStudents(true);
      const data = await PickupAPI.getSchoolStudentsBySchool(selectedSchoolId, { createdSource: 'pickup_enrollment' });
      setSchoolStudents(Array.isArray(data) ? data : []);
    } catch (e) {
      error('Failed to load school students');
    } finally {
      setLoadingStudents(false);
    }
  };

  // Load coaches
  const loadCoaches = async () => {
    try {
      const data = await getCoaches();
      
      // Filter for approved and active coaches with coach role
      const approvedCoaches = Array.isArray(data) ? data.filter(coach => {
        const isApproved = coach.status === 'approved' || coach.approvalStatus === 'approved' || coach.isApproved === true;
        const isActive = coach.isActive !== false;
        const isCoachRole = coach.role === 'coach';
        
        return isApproved && isActive && isCoachRole;
      })
        : [];
      setCoaches(approvedCoaches);
    } catch (e) {
      console.error('❌ Error loading coaches:', e);
      error('Failed to load coaches');
    }
  };

  // Load pickup rosters
  const loadPickupRosters = async () => {
    try {
      setLoadingRosters(true);
      const data = await PickupAPI.getRostersByCoach('', { includeStudents: true });
      setPickupRosters(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('❌ Error loading pickup rosters:', e);
      error('Failed to load pickup rosters');
    } finally {
      setLoadingRosters(false);
    }
  };

  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      await Promise.all([loadSchools(), loadCoaches()]);
      setLoading(false);
    };
    loadInitialData();
  }, []);

  useEffect(() => {
    // Load school students when school changes
    if (selectedSchoolId) {
      loadSchoolStudents();
    }
  }, [selectedSchoolId]);

  useEffect(() => {
    // Load pickup rosters when in view mode
    if (viewMode === 'view') {
      loadPickupRosters();
    }
  }, [viewMode]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isSchoolDropdownOpen && !event.target.closest('.school-dropdown-container')) {
        setIsSchoolDropdownOpen(false);
      }
      if (isCoachDropdownOpen && !event.target.closest('.coach-dropdown-container')) {
        setIsCoachDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSchoolDropdownOpen, isCoachDropdownOpen]);

  // Process students from schoolStudents
  const processedStudents = useMemo(() => {
    if (!schoolStudents.length) return [];

    const students = schoolStudents.map((student, idx) => {
      const gradeBand = mapGradeToBand(student?.grade);
      
      return {
        id: student.id || `student-${idx}`,
        studentId: student.id,
        firstName: student?.firstName || student?.studentFirstName || '',
        lastName: student?.lastName || student?.studentLastName || '',
        fullName: [
          student?.firstName || student?.studentFirstName || '',
          student?.lastName || student?.studentLastName || ''
        ].filter(Boolean).join(' ') || 'Unknown',
        grade: student?.grade || student?.studentGrade || '',
        gradeBand,
        age: student?.age || student?.studentAge || '',
        dob: student?.dob || student?.studentDob || '',
        school: selectedSchool,
        sports: Array.isArray(student?.sports) ? student.sports : [],
        parentFirstName: student?.parentFirstName || student?.parentGuardianFirstName || '',
        parentLastName: student?.parentLastName || student?.parentGuardianLastName || '',
        parentFullName: [
          student?.parentFirstName || student?.parentGuardianFirstName || '',
          student?.parentLastName || student?.parentGuardianLastName || ''
        ].filter(Boolean).join(' ') || '',
        parentEmail: student?.parentEmail || student?.parentGuardianEmail || '',
        parentPhone: student?.parentPhone || student?.parentGuardianPhone || '',
        createdSource: student?.createdSource || ''
      };
    });

    return students;
  }, [schoolStudents, selectedSchool]);

  // Get schools with pickup enrollment counts
  const schoolsWithEnrollmentCounts = useMemo(() => {
    // Since we now load students per school, we'll show 0 until a school is selected
    // The API doesn't provide a way to get all students across all schools at once
    return schools.map(school => ({
      ...school,
      enrollmentCount: 0 // Will be updated when school is selected
    }));
  }, [schools]);

  // Filter schools by search term
  const filteredSchools = useMemo(() => {
    if (!schoolSearchTerm) return schoolsWithEnrollmentCounts;
    const search = schoolSearchTerm.toLowerCase();
    return schoolsWithEnrollmentCounts.filter(school =>
      school.name.toLowerCase().includes(search)
    );
  }, [schoolsWithEnrollmentCounts, schoolSearchTerm]);

  // Filter coaches by search term
  const filteredCoaches = useMemo(() => {
    if (!coachSearchTerm) return coaches;
    const search = coachSearchTerm.toLowerCase();
    return coaches.filter(coach => {
      const fullName = `${coach.firstName} ${coach.lastName}`.toLowerCase();
      return fullName.includes(search);
    });
  }, [coaches, coachSearchTerm]);

  // Filter students
  const filteredStudents = useMemo(() => {
    let filtered = processedStudents;

    // Grade filter
    if (gradeFilter) {
      filtered = filtered.filter(student => student.gradeBand === gradeFilter);
    }

    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(student => 
        student.fullName.toLowerCase().includes(search) ||
        student.parentFullName.toLowerCase().includes(search) ||
        student.parentEmail.toLowerCase().includes(search) ||
        student.grade.toLowerCase().includes(search)
      );
    }

    return filtered;
  }, [processedStudents, gradeFilter, searchTerm]);

  const isSelected = (id) => selectedStudents.includes(id);
  const toggleStudentSelection = (studentId) => {
    setSelectedStudents(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const toggleAllStudents = () => {
    if (selectedStudents.length === filteredStudents.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(filteredStudents.map(s => s.id));
    }
  };

  // Create roster handler
  const handleCreateRoster = async () => {
    if (!selectedGradeBand || !selectedCoach || !rosterName || selectedStudents.length === 0) {
      warning('Please fill all fields and select at least one student');
      return;
    }

    try {
      setIsCreatingRoster(true);
      
      // Create roster data matching backend expectations
      const rosterData = {
        name: rosterName,
        gradeBand: selectedGradeBand,
        schoolId: selectedSchoolId,
        schoolName: selectedSchool,
        coachId: selectedCoach,
        coachName: coaches.find(c => c.id === selectedCoach)?.firstName + ' ' + coaches.find(c => c.id === selectedCoach)?.lastName || 'Unknown Coach',
        studentIds: selectedStudents,
        createdBy: adminId,
        createdAt: new Date().toISOString(),
        isActive: true
      };

      // Create roster via API
      const result = await PickupAPI.createRoster(rosterData);
      
      success(`Roster "${rosterName}" created successfully with ${selectedStudents.length} students`);
      
      // Reset modal
      setIsCreateRosterModalOpen(false);
      setSelectedGradeBand('');
      setSelectedCoach('');
      setRosterName('');
      setSelectedStudents([]);
      
    } catch (e) {
      console.error('❌ Error creating roster:', e);
      error('Failed to create roster: ' + e.message);
    } finally {
      setIsCreatingRoster(false);
    }
  };

  return (
    <div className="space-y-6">
      {loading ? (
        <>
          <Header
            title="School Rosters"
            subtitle="Loading schools and coaches..."
          />
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading schools and coaches data...</p>
            </div>
          </div>
        </>
      ) : (
        <>
          <Header
            title="School Rosters"
            subtitle="Create grade band rosters from pickup enrollments and assign to coaches"
          />

          {/* View Mode Toggle */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setViewMode('create')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    viewMode === 'create'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Users size={18} className="inline mr-2" />
                  Create Roster
                </button>
                <button
                  onClick={() => setViewMode('view')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    viewMode === 'view'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <UserCheck size={18} className="inline mr-2" />
                  View All Rosters
                </button>
              </div>
              {viewMode === 'view' && (
                <button
                  onClick={loadPickupRosters}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  Refresh
                </button>
              )}
            </div>
          </div>

          {/* View Mode: Pickup Rosters */}
          {viewMode === 'view' && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">All Pickup Rosters</h3>
              
              {loadingRosters ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-500">Loading rosters...</p>
                </div>
              ) : pickupRosters.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50">
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Roster Name</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Grade Band</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">School</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Coach</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Students</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Status</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pickupRosters.map((roster) => (
                        <tr key={roster.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <p className="font-medium text-gray-800">{roster.name}</p>
                          </td>
                          <td className="py-3 px-4">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {roster.gradeBand}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <p className="text-sm text-gray-600">{roster.schoolName}</p>
                          </td>
                          <td className="py-3 px-4">
                            <p className="text-sm text-gray-600">{roster.coachName || 'Unassigned'}</p>
                          </td>
                          <td className="py-3 px-4">
                            <p className="text-sm text-gray-600">{roster.studentCount || 0}</p>
                          </td>
                          <td className="py-3 px-4">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              roster.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                              {roster.status || 'Active'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <p className="text-sm text-gray-600">
                              {roster.createdAt ? new Date(roster.createdAt._seconds * 1000).toLocaleDateString() : '—'}
                            </p>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Users size={48} className="mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500">No rosters created yet</p>
                </div>
              )}
            </div>
          )}

          {/* Create Mode: Controls */}
          {viewMode === 'create' && (
            <>
      {/* Controls */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* School Selection */}
          <div className="school-dropdown-container">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select School
            </label>
            <div className="relative">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  value={selectedSchool || schoolSearchTerm}
                  onChange={(e) => {
                    setSchoolSearchTerm(e.target.value);
                    setIsSchoolDropdownOpen(true);
                  }}
                  onFocus={() => setIsSchoolDropdownOpen(true)}
                  placeholder="Search schools..."
                  className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {(schoolSearchTerm || selectedSchool) && (
                  <button
                    onClick={() => {
                      setSchoolSearchTerm('');
                      setSelectedSchool('');
                      setSelectedSchoolId('');
                    }}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <XCircle size={18} />
                  </button>
                )}
              </div>
              {isSchoolDropdownOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {filteredSchools.length === 0 ? (
                    <div className="p-3 text-gray-500 text-sm">No schools found</div>
                  ) : (
                    filteredSchools.map(school => (
                      <div
                        key={school.id}
                        onClick={() => {
                          setSelectedSchool(school.name);
                          setSelectedSchoolId(school.id);
                          setSchoolSearchTerm('');
                          setIsSchoolDropdownOpen(false);
                        }}
                        className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                      >
                        {school.name}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Grade Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Grade Band
            </label>
            <select
              value={gradeFilter}
              onChange={(e) => setGradeFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Grades</option>
              {GRADE_BANDS.map(band => (
                <option key={band} value={band}>
                  {band}
                </option>
              ))}
            </select>
          </div>

          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search Students
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, parent, grade..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        {selectedSchool && (
          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
            <div className="flex items-center gap-4">
              <div className="text-sm text-gray-600">
                {filteredStudents.length} student{filteredStudents.length !== 1 ? 's' : ''} found
                {selectedStudents.length > 0 && ` • ${selectedStudents.length} selected`}
              </div>
              {filteredStudents.length > 0 && (
                <button
                  onClick={toggleAllStudents}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  {selectedStudents.length === filteredStudents.length ? 'Deselect All' : 'Select All'}
                </button>
              )}
            </div>
            
            {selectedStudents.length > 0 && (
              <Button
                onClick={() => {
                  // Pre-select the current grade filter in the modal
                  if (gradeFilter) {
                    setSelectedGradeBand(gradeFilter);
                  }
                  setIsCreateRosterModalOpen(true);
                }}
                className="flex items-center gap-2"
              >
                <Users size={18} />
                Create Roster ({selectedStudents.length})
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Students Table */}
      {!selectedSchool ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <School className="mx-auto h-12 w-12 text-gray-300 mb-4" />
          <div className="text-lg font-medium text-gray-800">Select a School</div>
          <div className="text-sm text-gray-500 mt-2">
            Please select a school from the dropdown above to view students
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {loadingStudents ? (
            <div className="p-8 text-center text-gray-500">
              <div className="animate-pulse">Loading students...</div>
            </div>
          ) : filteredStudents.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <GraduationCap className="mx-auto h-12 w-12 text-gray-300 mb-4" />
              <div className="text-lg font-medium">No students found</div>
              <div className="text-sm text-gray-400 mt-1">
                {!gradeFilter && !searchTerm 
                  ? 'No students from this school with pickup enrollment source found'
                  : gradeFilter && !searchTerm
                  ? `No students found in ${gradeFilter}. Try selecting a different grade band or "All Grades".`
                  : searchTerm
                  ? 'No students match your search. Try different keywords.'
                  : 'Try adjusting your filters'
                }
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedStudents.length === filteredStudents.length && filteredStudents.length > 0}
                        onChange={toggleAllStudents}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Student
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Grade
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Grade Band
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Parent
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Sports
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredStudents.map((student) => (
                    <tr key={student.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedStudents.includes(student.id)}
                          onChange={() => toggleStudentSelection(student.id)}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {student.fullName}
                        </div>
                        {student.age && (
                          <div className="text-xs text-gray-500">Age: {student.age}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {student.grade || '—'}
                      </td>
                      <td className="px-6 py-4">
                        {student.gradeBand ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {student.gradeBand}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {student.parentFullName || '—'}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {student.parentEmail || '—'}
                        </div>
                        {student.parentPhone && (
                          <div className="text-xs text-gray-500">{student.parentPhone}</div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {student.sports.length > 0 ? (
                            student.sports.map((sport, idx) => (
                              <span
                                key={idx}
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800"
                              >
                                {sport}
                              </span>
                            ))
                          ) : (
                            <span className="text-sm text-gray-400">—</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
            </>
          )}

      {/* Create Roster Modal */}
      <Modal
        isOpen={isCreateRosterModalOpen}
        onClose={() => setIsCreateRosterModalOpen(false)}
        title="Create Grade Band Roster"
        size="lg"
      >
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Grade Band
              </label>
              <select
                value={selectedGradeBand}
                onChange={(e) => setSelectedGradeBand(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select grade band...</option>
                {GRADE_BANDS.map(band => (
                  <option key={band} value={band}>{band}</option>
                ))}
              </select>
            </div>

            <div className="coach-dropdown-container">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Assign Coach
              </label>
              <div className="relative">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    value={selectedCoach ? coaches.find(c => c.id === selectedCoach)?.firstName + ' ' + coaches.find(c => c.id === selectedCoach)?.lastName : coachSearchTerm}
                    onChange={(e) => {
                      setCoachSearchTerm(e.target.value);
                      setIsCoachDropdownOpen(true);
                    }}
                    onFocus={() => setIsCoachDropdownOpen(true)}
                    placeholder="Search coaches..."
                    className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  {(coachSearchTerm || selectedCoach) && (
                    <button
                      onClick={() => {
                        setCoachSearchTerm('');
                        setSelectedCoach('');
                      }}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <XCircle size={18} />
                    </button>
                  )}
                </div>
                {isCoachDropdownOpen && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredCoaches.length === 0 ? (
                      <div className="p-3 text-gray-500 text-sm">No coaches found</div>
                    ) : (
                      filteredCoaches.map(coach => (
                        <div
                          key={coach.id}
                          onClick={() => {
                            setSelectedCoach(coach.id);
                            setCoachSearchTerm('');
                            setIsCoachDropdownOpen(false);
                          }}
                          className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                        >
                          {coach.firstName} {coach.lastName} ({coach.email})
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Roster Name
            </label>
            <TextField
              value={rosterName}
              onChange={(e) => setRosterName(e.target.value)}
              placeholder="e.g., Allenwood K-1st Grade Roster"
            />
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Users className="h-5 w-5 text-blue-600" />
              <span className="font-medium text-gray-900">
                Selected Students ({selectedStudents.length})
              </span>
            </div>
            <div className="text-sm text-gray-600">
              {selectedStudents.length > 0 ? (
                <div>
                  Creating roster for <span className="font-medium">{selectedSchool}</span> with{' '}
                  <span className="font-medium">{selectedStudents.length}</span> students
                </div>
              ) : (
                <div className="text-red-600">
                  Please select at least one student to create a roster
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <Button
              variant="secondary"
              onClick={() => setIsCreateRosterModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateRoster}
              disabled={isCreatingRoster || !selectedGradeBand || !selectedCoach || !rosterName || selectedStudents.length === 0}
            >
              {isCreatingRoster ? 'Creating...' : 'Create Roster'}
            </Button>
          </div>
        </div>
      </Modal>
        </>
      )}
    </div>
  );
};

export default SchoolRosters;
